import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/common/toast';
import { AsyncButton } from '@/components/common/AsyncButton';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { Modal } from '@/components/dialogs/Modal';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { Select2 } from '@/components/forms/Select2';
import { ApiError } from '@/api/client/abpError';
import { usePermission } from '@/permissions/usePermission';
import { executionVenuesApi } from '@/api/services/onboarding';
import type {
  CreateExecutionVenueDto,
  ExecutionVenueDatatableDto,
  ExecutionVenueDto,
} from '@/api/types/onboarding';

const P = 'User.Onboarding.ExecutionVenues';

interface NewRow {
  leiCode: string;
  nameOfEntity: string;
  country: string; // countryId
  typeOfEntity: string;
  relation: string;
}
interface EditRow {
  country: string;
  countryId: number;
  typeOfEntity: string;
  relation: string;
}

export function ExecutionVenuesTab() {
  const toast = useToast();
  const can = usePermission();
  const canCreate = can(`${P}.Create`);
  const canUpdate = can(`${P}.Update`);
  const canDelete = can(`${P}.Delete`);
  const canDownloadExcel = can(`${P}.DownloadExcel`);

  const dropdowns = useQuery({
    queryKey: ['onboarding', 'exec-venues', 'dropdown'],
    queryFn: executionVenuesApi.getDropdownData,
  });
  const opts = dropdowns.data;

  useEffect(() => {
    if (dropdowns.isError) toast.error('Failed to load dropdown data');
  }, [dropdowns.isError, toast]);

  const [newRow, setNewRow] = useState<NewRow | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editRow, setEditRow] = useState<EditRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ExecutionVenueDto | null>(null);
  const [pending, setPending] = useState<CreateExecutionVenueDto | null>(null);
  const [ddParent, setDdParent] = useState<HTMLElement | null>(null);

  const table = useServerDataTable<ExecutionVenueDto, Partial<ExecutionVenueDatatableDto>>({
    queryKey: ['onboarding', 'exec-venues'],
    fetcher: (req) => executionVenuesApi.getListForDataTable(req as ExecutionVenueDatatableDto),
    pageSize: 5,
  });

  const countryOpts = (opts?.countries ?? []).map((c) => ({ value: c.id, label: c.name }));
  const typeOpts = (opts?.typesOfEntity ?? []).map((t) => ({ value: t.value, label: t.name }));
  const relationOpts = (opts?.relations ?? []).map((r) => ({ value: r.value, label: r.name }));

  const saveNew = async () => {
    if (!newRow) return;
    setSaving(true);
    try {
      const dto: CreateExecutionVenueDto = {
        leiCode: newRow.leiCode,
        nameOfEntity: newRow.nameOfEntity,
        countryId: Number.parseInt(newRow.country, 10),
        typeOfEntity: Number.parseInt(newRow.typeOfEntity, 10),
        relation: Number.parseInt(newRow.relation, 10),
      };
      const result = await executionVenuesApi.create(dto);
      if (result.status === 'Created') {
        toast.success(result.message);
        setNewRow(null);
        table.refetch();
      } else if (result.status === 'PendingConfirmation') {
        setPending(result.pendingData);
      } else {
        console.warn('Unknown create status', result.status);
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to create Execution Venue');
    } finally {
      setSaving(false);
    }
  };

  const confirmCreate = async () => {
    if (!pending) return;
    try {
      await executionVenuesApi.confirmCreate(pending);
      toast.success('Execution Venue created successfully');
      setPending(null);
      setNewRow(null);
      table.refetch();
    } catch {
      toast.error('Failed to create Execution Venue');
      setPending(null);
    }
  };

  const beginEdit = (row: ExecutionVenueDto) => {
    setEditId(row.id);
    setEditRow({
      country: String(row.countryId),
      countryId: row.countryId,
      typeOfEntity: String(typeOpts.find((t) => t.label === row.typeOfEntity)?.value ?? ''),
      relation: String(relationOpts.find((r) => r.label === row.relation)?.value ?? ''),
    });
  };

  const saveEdit = async () => {
    if (!editId || !editRow) return;
    setSaving(true);
    try {
      const countryParsed = Number.parseInt(editRow.country, 10);
      await executionVenuesApi.update(editId, {
        countryId: Number.isNaN(countryParsed) ? editRow.countryId : countryParsed,
        typeOfEntity: Number.parseInt(editRow.typeOfEntity, 10),
        relation: Number.parseInt(editRow.relation, 10),
      });
      toast.success('Saved successfully.');
      setEditId(null);
      setEditRow(null);
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to update Execution Venue');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await executionVenuesApi.delete(deleteTarget.id);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to delete.');
    } finally {
      setDeleteTarget(null);
      table.refetch();
    }
  };

  const sel = (val: string, options: { value: string | number; label: string }[], onChange: (v: string) => void) => (
    <Select2
      value={val}
      options={options}
      placeholder="N/A"
      allowClear={false}
      searchable={false}
      disabled={saving}
      dropdownParent={ddParent}
      onChange={(v) => onChange(v ?? '')}
    />
  );

  const columns = useMemo<DataGridColumn<ExecutionVenueDto>[]>(
    () => [
      { key: 'nameOfEntity', title: 'Name Of Entity', className: 'text-start', value: (r) => r.nameOfEntity },
      { key: 'leiCode', title: 'LEI Code', className: 'text-start', value: (r) => r.leiCode },
      {
        key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country,
        cell: (r) => (editId === r.id && editRow ? sel(editRow.country, countryOpts, (v) => setEditRow({ ...editRow, country: v })) : r.country),
      },
      {
        key: 'typeOfEntity', title: 'Type Of Entity', className: 'text-start', value: (r) => r.typeOfEntity,
        cell: (r) => (editId === r.id && editRow ? sel(editRow.typeOfEntity, typeOpts, (v) => setEditRow({ ...editRow, typeOfEntity: v })) : r.typeOfEntity),
      },
      {
        key: 'relation', title: 'Relation', className: 'text-start', value: (r) => r.relation,
        cell: (r) => (editId === r.id && editRow ? sel(editRow.relation, relationOpts, (v) => setEditRow({ ...editRow, relation: v })) : r.relation),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editId, editRow, saving, opts],
  );

  const toolbar = canCreate ? (
    <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-plus" title="Create new Execution Venue" onClick={() => { if (!newRow) setNewRow({ leiCode: '', nameOfEntity: '', country: '', typeOfEntity: '', relation: '' }); }}>
      Create New
    </AsyncButton>
  ) : null;

  const footerTop = newRow ? (
    <tr className="onb-new-row">
      <td><input className="form-control form-control-sm" placeholder="Name Of Entity" value={newRow.nameOfEntity} disabled={saving} onChange={(e) => setNewRow({ ...newRow, nameOfEntity: e.target.value })} /></td>
      <td><input className="form-control form-control-sm" placeholder="LEI Code" value={newRow.leiCode} disabled={saving} onChange={(e) => setNewRow({ ...newRow, leiCode: e.target.value })} /></td>
      <td>{sel(newRow.country, countryOpts, (v) => setNewRow({ ...newRow, country: v }))}</td>
      <td>{sel(newRow.typeOfEntity, typeOpts, (v) => setNewRow({ ...newRow, typeOfEntity: v }))}</td>
      <td>{sel(newRow.relation, relationOpts, (v) => setNewRow({ ...newRow, relation: v }))}</td>
      <td>
        <div className="d-flex gap-1">
          <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={saveNew} disabled={saving} />
          <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={() => setNewRow(null)}><i className="fa fa-times" /></button>
        </div>
      </td>
    </tr>
  ) : undefined;

  const rowActions = (canUpdate || canDelete)
    ? (row: ExecutionVenueDto) => (
        <div className="d-flex gap-1">
          {canUpdate && editId !== row.id && (
            <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => beginEdit(row)}><i className="fa fa-pencil" /></button>
          )}
          {editId === row.id && (
            <>
              <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={saveEdit} disabled={saving} />
              <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={() => { setEditId(null); setEditRow(null); }}><i className="fa fa-times" /></button>
            </>
          )}
          {canDelete && editId !== row.id && (
            <button type="button" className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => setDeleteTarget(row)}><i className="fa fa-trash" /></button>
          )}
        </div>
      )
    : undefined;

  return (
    <div ref={setDdParent}>
      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => r.id}
        card
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={table.setColumnFilter}
        toolbarStart={toolbar}
        includeExcel={canDownloadExcel}
        excelFileName="Execution Venue"
        footerTop={footerTop}
        rowActions={rowActions}
        rowClassName={(r) => (editId === r.id ? 'sfc-detail-editing' : undefined)}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data available'}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
      />

      <Modal
        show={pending != null}
        title="Confirm"
        onClose={() => setPending(null)}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setPending(null)}>No</button>
            <AsyncButton className="btn btn-primary" onClick={confirmCreate}>Yes</AsyncButton>
          </>
        }
      >
        The Lei Code you have entered does not appear to be correct. Are you sure you want to proceed anyway ?
      </Modal>

      <ConfirmDialog
        show={deleteTarget != null}
        message={deleteTarget ? `Delete Execution Venue '${deleteTarget.nameOfEntity}'?` : ''}
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
